import React, { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// material-ui
import { styled, useTheme, Theme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar } from '@mui/material';

// project imports
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import Header from './Header';
import Sidebar from './Sidebar';
import navigation from 'menu-items';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import { useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons';
import { isPoetProject } from 'utils';

const isPoet = isPoetProject();

interface MainStyleProps {
    theme: Theme;
    open: boolean;
}

// styles
const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }: MainStyleProps) => ({
    ...theme.typography.mainContent,
    ...(!open && {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter
        }),
        [theme.breakpoints.up('md')]: {
            marginLeft: -(drawerWidth - 20),
            width: `calc(100% - ${drawerWidth}px)`
        },
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px',
            width: `calc(100% - ${drawerWidth}px)`,
            padding: '16px',
            marginRight: '10px'
        }
    }),
    ...(open && {
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter
        }),
        marginLeft: 0,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        [theme.breakpoints.down('md')]: {
            marginLeft: '20px'
        },
        [theme.breakpoints.down('sm')]: {
            marginLeft: '10px'
        }
    })
}));

// ==============================|| MAIN LAYOUT ||============================== //

const MainLayout = () => {
    const theme = useTheme();
    // Use this if we want to dispatch the drawer state with the breakpoint
    // const matchDownMd = useMediaQuery(theme.breakpoints.down('lg'));
    // const dispatch = useDispatch();
    // React.useEffect(() => {
    //     dispatch(openDrawer(!matchDownMd));
    // }, [dispatch, matchDownMd]);
    const { drawerOpen } = useSelector((state) => state.menu);
    const { container } = useConfig();

    const header = useMemo(
        () => (
            <Toolbar sx={{ paddingBottom: 0 }}>
                <Header />
            </Toolbar>
        ),
        []
    );

    /*
        CENTRALIZED PAGE TITLES MANAGEMENT
        WE HAVE A COUPLE OF OPTIONS HERE TO ACHIVE THE ABOVE
        1- ADAPT REACT ROUTER TO MAKE IT A DATA ROUTER & THEN USE THE HANDLE PROPERTY TO DEFINE THE TITLES (PROBLEM IS WE ALSO HAVE DYNAMIC ROUTES (RENDER MODULE))
        2- DEFINE A PAGE TITLE MAP AND USE THAT - PROBLEM WOULD BE, NO DYNAMIC SUPPORT, EVERYTIME WE ADD A NEW ROUTE WE WILL NEED TO ADD IT IN THE MAP
        3- USE LOCATION TO GET THE VIEW FROM THE URL AND ADJUST IT FOR BETTER USABILITY. FOR THE SPECIFIC COMPONENTS WHICH LOAD DATA FROM BE, WE CAN USE A DIRECT HELMET WHICH WOULD OVERRIDE THE GLOBAL ONE, EXAMPLE- RECORD GRID
    */

    const location = useLocation();
    const pageTitle = location.pathname
        .replace(/\/+$/, '') // truncates the slash in the end to make sure we have accounted for query params
        .split('/')
        ?.at(-1) // returns the last param which is 99% is what belongs to the view
        ?.split('-')
        .join(' ') // adds spaces in between the str
        .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalizes the first character and the word boundary

    return (
        <Box sx={{ display: 'flex' }}>
            {/* beamer embed */}
            <Helmet>
                <title>{`OrderBahn${pageTitle ? ` - ${pageTitle}` : ''}`}</title>
                <script>
                    {`var beamer_config = {
                            product_id: '${process.env.REACT_APP_BEAMER_PRODUCT_ID}'
                        };`}
                </script>
                <script type="text/javascript" src="https://app.getbeamer.com/js/beamer-embed.js" defer />
            </Helmet>

            <CssBaseline />
            {/* header */}
            <AppBar
                enableColorOnDark
                position="fixed"
                color="inherit"
                elevation={0}
                sx={{
                    bgcolor: isPoet ? theme.palette.secondary.main : theme.palette.background.default,
                    boxShadow: isPoet ? '0px 4px 4px rgba(0, 0, 0, 0.25)' : 'none',
                    transition: drawerOpen ? theme.transitions.create('width') : 'none',
                    paddingBottom: '0 !important'
                }}
            >
                {header}
            </AppBar>

            {/* drawer */}
            <Sidebar />

            {/* main content */}
            <Main theme={theme} open={drawerOpen} style={{ padding: '0', maxHeight: 'calc(100vh - 88px)' }}>
                {/* breadcrumb */}
                {container && (
                    <Container maxWidth="lg">
                        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                        <Outlet />
                    </Container>
                )}
                {!container && (
                    <>
                        <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                        <Outlet />
                    </>
                )}
            </Main>
        </Box>
    );
};

export default MainLayout;
